import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import Scroll from 'react-scroll';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import ServicesForm from '../ServicesForm';
import Triple from '../content/Triple';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import SiteRequirements from '../content/SiteRequirements';
import Testimonials from '../content/Testimonials';
import ResidentialFaq from '../faqs/ResidentialFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';
import CDN_BASE from '../../../utils/cdn';

const featuredTrucks = { featured: true };

function Residential() {
  const config = useContext(ConfigContext);
  const {
    vehicle_type, city_name, site_title,
  } = config;
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();

  // CMS-driven copy. `cms` is null when no published row exists for this city,
  // so every reference uses optional chaining and falls back to the original
  // hardcoded JSX. Seattle has no row → renders unchanged. Austin has bespoke
  // copy → renders the CMS content with {{tokens}} interpolated.
  const { content: cms } = usePageContent('services_residential');

  return (
    <div>
      <Meta
        title={cms?.meta?.title || `Food ${vehicleType}s for apartment communities in ${city_name}`}
        breadcrumbName='Residential Services'
        type='website'
        description={cms?.meta?.description || `Bring food ${vehicleType}s to your apartment or HOA community in ${city_name}. Schedule rotating food ${vehicleType} visits for residents with weekly menus.`}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-residential-food-truck-services.webp`} alt='Food truck serving a residential community' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `Food ${vehicleType}s for your apartment community`}</h1>
            <p>{cms?.hero?.subheadline || `Give residents a rotating lineup of local food ${vehicleType}s. We build the schedule and coordinate with the ${vehicleType}s.`}</p>
            <p>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                {cms?.hero?.cta_label || `Set up resident food ${vehicleType} service`}
              </Scroll.Link>
            </p>
          </div>
        </div>
      </div>

      <div className='Section'>
        <LogoList residential title='Communities we work with' />
      </div>

      <section className='Section'>
        <h2>{cms?.how_it_works?.heading || 'How it works'}</h2>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-scheduled-food-truck-service.webp`}
            isResponsive
            alt='Food truck schedule calendar'
            title={cms?.how_it_works?.card1_title || 'We build the rotation'}
            text={cms?.how_it_works?.card1_body || `Choose the days and service window. We coordinate the ${vehicleType}s and keep the schedule current.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-food-truck-menu-email.webp`}
            isResponsive
            alt='Weekly menu email for residents'
            title={cms?.how_it_works?.card2_title || 'Residents know what’s coming'}
            text={cms?.how_it_works?.card2_body || `Share one page with upcoming ${vehicleType}s and menus.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-mobile-food-truck-service.webp`}
            isResponsive
            alt='Food truck serving residents'
            title={cms?.how_it_works?.card3_title || 'One point of contact'}
            text={cms?.how_it_works?.card3_body || `Questions, schedule changes, and vendor coordination come through ${site_title}, not your leasing office.`}
          />
        </div>
      </section>

      <FeaturedTrucks
        title={`Featured food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <section className='Section--extraPadding'>
        <h2>{cms?.benefits?.heading || 'What to expect'}</h2>
        {/* Fallback cards answer the bible §22 checklist; the CMS keys keep their legacy names. */}
        <div className={tripleStyles.Triple_wrapper + ' ' + tripleStyles.Triple_wrapper__quad}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-employee-food-truck-perk.webp`}
            isResponsive
            alt='Food truck parked at an apartment community'
            title={cms?.benefits?.attract_title || 'What we need from you'}
            text={cms?.benefits?.attract_body || `A place for the ${vehicleType} to park, a service window, and basic site-access details. We’ll work through the rest with you.`}
          />
          {/* Pricing per the client (4 Sep 2026); cancellations per Master Policies v1.0 §11. */}
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-workplace-food-truck.webp`}
            isResponsive
            alt='Residents ordering at a food truck'
            title={cms?.benefits?.retain_title || 'What it costs'}
            text={cms?.benefits?.retain_body || `Most properties have us run it. Hosted service is a monthly program fee we quote up front, and we handle the schedule, the ${vehicleType}s, and the day-of details. Prefer to run it yourself? Submit a recurring request through the catering form and pick your own ${vehicleType}s. There’s no program fee that way.`}
          >
            {/* PS3. Recurrence is the Repeats mode on the wizard's date step;
                ?mode=repeats opens the form on that step with Repeats
                selected (backlog E3; design CR 70 decides whether the
                parameter or a plain link is drawn). */}
            <div className={tripleStyles.TripleActions}>
              <Link to='/catering/requests/new' className='Button'>Get a quote</Link>
              <Link to='/catering/requests/new?mode=repeats' className='Button Button--outlined'>Start a recurring request</Link>
            </div>
          </Triple>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-team-food-truck-lunch.webp`}
            isResponsive
            alt='Residents eating food truck food together'
            title={cms?.benefits?.costs_title || `If a ${vehicleType} cancels`}
            text={cms?.benefits?.costs_body || `We look for another ${vehicleType} first. A ${vehicleType} that cancels late pays a fee, and your schedule updates the moment anything changes.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-local-food-truck-vendors.webp`}
            isResponsive
            alt='Local food truck vendor in the community'
            title={cms?.benefits?.local_title || 'One evening or a standing rotation'}
            text={cms?.benefits?.local_body || 'Try a single visit first, or set a weekly rotation from the start.'}
          />
        </div>
      </section>


      <SiteRequirements />

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-residential-food-truck-benefits.webp`} alt='Resident credit packages and rewards program' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.credits?.heading || 'Resident credits and move-in perks'}</h3>
            <CmsRichText
              html={cms?.credits?.body}
              fallback={<p>Want to sweeten a renewal or welcome new residents? We can set up resident credit packages so the {vehicleType}s become a perk with a dollar value attached.</p>}
            />
            <div>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                {cms?.credits?.cta_label || 'Ask about credits'}
              </Scroll.Link>
            </div>
          </div>
        </div>
      </section>

      <Testimonials title='What people are saying' />

      <section className='Section--extraPadding'>
        <ResidentialFaq />
      </section>

      <Scroll.Element name='form'>
        <div className='GrayBg'>
          <section className='Section--extraPadding'>
            <h2>{cms?.form?.heading || 'Tell us about your community'}</h2>
            <ServicesForm formType='residential' />
          </section>
        </div>
      </Scroll.Element>
    </div>
  );
}

export default Residential;
